/**
 * Email Test Page
 * 
 * This page provides a testing interface for the email functionality
 * of the Meduler application. It allows administrators to verify that
 * email notifications are working correctly with the Resend API.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import EmailTestComponent from '@/components/EmailTest/EmailTestComponent';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { APP_NAME } from '@/constants';
import { Codebox } from '@/components/ui/codebox';
import { useMemo } from 'react';

/**
 * Page for testing email functionality
 */
const EmailTestPage: React.FC = () => {
  // Sample .env content for documentation purposes
  const envSample = useMemo(() => (
`# Resend API Configuration
VITE_RESEND_API_KEY=re_yourapikey

# Other environment variables
VITE_API_BASE_URL=https://api.example.com
`), []);

  return (
    <>
      <Helmet>
        <title>Email Testing | {APP_NAME}</title>
      </Helmet>
      
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-8">Email Testing Dashboard</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs defaultValue="send-test">
              <TabsList className="mb-4">
                <TabsTrigger value="send-test">Send Test Email</TabsTrigger>
                <TabsTrigger value="documentation">Documentation</TabsTrigger>
              </TabsList>
              
              <TabsContent value="send-test">
                <EmailTestComponent />
              </TabsContent>
              
              <TabsContent value="documentation">
                <Card>
                  <CardHeader>
                    <CardTitle>Email Configuration Guide</CardTitle>
                    <CardDescription>
                      How to set up and use the email functionality in Meduler
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <h3 className="text-lg font-medium">Setting up Resend API</h3>
                    <ol className="list-decimal ml-6 space-y-2">
                      <li>Create an account at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Resend.com</a></li>
                      <li>Generate an API key from your Resend dashboard</li>
                      <li>Add the API key to your <code>.env</code> file as shown below</li>
                    </ol>
                    
                    <h3 className="text-lg font-medium mt-4">Environment Configuration</h3>
                    <p>Create or update your <code>.env</code> file with the following:</p>
                    <Codebox language="bash">{envSample}</Codebox>
                    
                    <h3 className="text-lg font-medium mt-4">Using Email Service</h3>
                    <p>Import the email service in your components:</p>
                    <Codebox language="typescript">
{`import { sendEmail } from '@/utils/emailService';

// Example usage
await sendEmail({
  to: 'recipient@example.com',
  subject: 'Important Notification',
  html: '<p>Hello from Meduler!</p>',
});`}
                    </Codebox>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Email Features</CardTitle>
                <CardDescription>
                  Overview of email capabilities in Meduler
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium">Notification Emails</h3>
                    <p className="text-sm text-muted-foreground">Automated emails for medication reminders and important updates.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-medium">Responsive Templates</h3>
                    <p className="text-sm text-muted-foreground">Mobile-friendly email templates that look great on all devices.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-medium">Delivery Analytics</h3>
                    <p className="text-sm text-muted-foreground">Track open rates and delivery status through the Resend dashboard.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-medium">Custom Branding</h3>
                    <p className="text-sm text-muted-foreground">Emails are styled to match Meduler's brand identity.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailTestPage;
